import { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, Lightformer, ContactShadows } from "@react-three/drei";
import { XR, XROrigin } from "@react-three/xr";
import { xrStore } from "@/components/three/xrStore";
import * as THREE from "three";
import { useRef, useEffect, useState, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import { useStore, PRODUCTS, RING_VARIANTS, MELASYL_MODELS } from "@/store/useStore";
import ProductModel from "@/components/three/ProductModel";
import LotusTryOn from "@/components/three/LotusTryOn";
import SmartRoom from "@/components/three/SmartRoom";
import InSceneFlashlight from "@/components/three/InSceneFlashlight";
import SnapTurn from "@/components/three/SnapTurn";
import HeadsetControls from "@/components/three/HeadsetControls";
import TeleportFloor from "@/components/three/TeleportFloor";
import { SkinSurface } from "@/components/three/Helpers";

function CameraRig() {
  const controls = useRef();
  const { camera } = useThree();
  const resetKey = useStore((s) => s.resetKey);
  const autoRotate = useStore((s) => s.autoRotate);
  const rotateHold = useStore((s) => s.rotateHold);
  const rotateSpeed = useStore((s) => s.rotateSpeed);

  useEffect(() => {
    camera.position.set(0, 0.4, 6);
    if (controls.current) {
      controls.current.target.set(0, 0, 0);
      controls.current.update();
    }
  }, [resetKey, camera]);

  // Pause auto-rotation while the user drags; resume 3s after release.
  useEffect(() => {
    const c = controls.current;
    if (!c) return;
    const onStart = () => useStore.getState().holdRotation();
    const onEnd = () => useStore.getState().bump();
    c.addEventListener("start", onStart);
    c.addEventListener("end", onEnd);
    return () => {
      c.removeEventListener("start", onStart);
      c.removeEventListener("end", onEnd);
    };
  }, []);

  return (
    <OrbitControls
      ref={controls}
      makeDefault
      enablePan={false}
      enableZoom
      enableRotate
      enableDamping
      dampingFactor={0.06}
      minDistance={2.5}
      maxDistance={11}
      autoRotate={autoRotate && !rotateHold}
      autoRotateSpeed={rotateSpeed * 1.4}
    />
  );
}

export default function Scene() {
  const product = useStore((s) => s.product);
  const variant = useStore((s) => s.variant);
  const melasylState = useStore((s) => s.melasylState);
  const tryOn = useStore((s) => s.tryOn);
  const dropperActive = useStore((s) => s.dropperActive);
  const flashlight = useStore((s) => s.flashlight);

  let url = PRODUCTS[product].url;
  if (product === "lotus") url = RING_VARIANTS[variant].url;
  if (product === "melasyl") url = melasylState === "closed" ? MELASYL_MODELS.closed : MELASYL_MODELS.open;

  // Serve lighter mobile LOD variants (*_lo.glb) on small / coarse-pointer devices.
  const isMobile = useMemo(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(max-width: 820px)").matches || window.matchMedia?.("(pointer: coarse)").matches),
    []
  );
  if (isMobile && url) url = url.replace(/\.glb$/, "_lo.glb");

  const dim = product === "lotus" && flashlight ? 0.32 : 1;
  const airpods = product === "airpods";

  const [teleport, setTeleport] = useState(() => new THREE.Vector3(0, 0, 0));
  const [rotY, setRotY] = useState(0);

  return (
    <Canvas
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      camera={{ position: [0, 0.4, 6], fov: 42 }}
      onCreated={({ gl }) => {
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.15;
      }}
      data-testid="3d-canvas-viewport"
    >
      <XR store={xrStore}>
        <XROrigin position={teleport} rotation={[0, rotY, 0]} />
        <SnapTurn onTurn={(d) => setRotY((r) => r + d)} />
        <HeadsetControls />
        <color attach="background" args={["#05070d"]} />
        <fog attach="fog" args={["#05070d", 9, 18]} />

        {airpods ? (
          /* Neutral white studio lighting so the glossy-white case reads pure white */
          <>
            <ambientLight intensity={0.95} color={"#ffffff"} />
            <directionalLight position={[4, 5, 3]} intensity={2.6} color={"#ffffff"} />
            <directionalLight position={[-5, 2, -2]} intensity={1.7} color={"#f2f5fa"} />
            <directionalLight position={[0, 6, -4]} intensity={1.3} color={"#ffffff"} />
            <pointLight position={[0, -3, 2]} intensity={0.5} color={"#cfd6e2"} />
          </>
        ) : (
          <>
            <ambientLight intensity={0.5 * dim} color={"#223044"} />
            <directionalLight position={[4, 5, 3]} intensity={2.2 * dim} color={"#00f0ff"} />
            <directionalLight position={[-5, 2, -2]} intensity={1.8 * dim} color={"#a855f7"} />
            <directionalLight position={[0, 6, -4]} intensity={1.1 * dim} color={"#ffffff"} />
            <pointLight position={[0, -3, 2]} intensity={0.6 * dim} color={"#0a1733"} />
          </>
        )}

        <Suspense fallback={null}>
          <group key={url}>
            <ProductModel url={url} />
          </group>

          {product === "lotus" && tryOn && <LotusTryOn />}
          {product === "lotus" && tryOn && <SmartRoom />}
          {product === "lotus" && <InSceneFlashlight />}
          {product === "melasyl" && dropperActive && <SkinSurface />}

          {airpods ? (
            <Environment resolution={256} frames={1}>
              <Lightformer intensity={2.2} position={[0, 4, 2]} scale={[8, 3, 1]} color="#ffffff" />
              <Lightformer intensity={1.6} position={[-4, 1, 2]} scale={[4, 4, 1]} color="#eef2f8" />
              <Lightformer intensity={1.6} position={[4, 1, -2]} scale={[4, 4, 1]} color="#ffffff" />
              <Lightformer intensity={0.8} position={[0, -3, 1]} scale={[8, 3, 1]} color="#2a3140" />
            </Environment>
          ) : (
            <Environment resolution={256} frames={1}>
              <Lightformer intensity={2} position={[0, 4, 2]} scale={[8, 3, 1]} color="#00f0ff" />
              <Lightformer intensity={1.6} position={[-4, 1, 2]} scale={[4, 4, 1]} color="#a855f7" />
              <Lightformer intensity={1.4} position={[4, 1, -2]} scale={[4, 4, 1]} color="#ffffff" />
              <Lightformer intensity={1} position={[0, -3, 1]} scale={[8, 3, 1]} color="#14203a" />
            </Environment>
          )}

          <ContactShadows
            position={[0, -1.9, 0]}
            opacity={0.45}
            scale={14}
            blur={2.6}
            far={5}
            color={airpods ? "#1a1f2b" : "#00121a"}
          />

          {/* VR teleport surface + markers (only visible inside an XR session) */}
          <TeleportFloor onTeleport={setTeleport} />
        </Suspense>

        <CameraRig />
      </XR>
    </Canvas>
  );
}
